/**
 * FREAK-ISH — Free Shipping Progress Bar (fallback updater)
 * =========================================================
 * THIS IS NOT THE PRIMARY RENDER PATH.
 *
 * The bar is server-rendered by snippets/freakish-shipping-bar.liquid, which
 * sits inside .cart-items__wrapper. Horizon's assets/component-cart-items.js
 * posts to Theme.routes.cart_change_url with a `sections=` param covering every
 * cart-items-component on the page and morphs the response back in, so every
 * add / remove / quantity change re-runs that Liquid server-side. Both the
 * drawer and the /cart page stay correct and in sync with no help from here.
 *
 * This file exists only for cart mutations that bypass that flow, i.e. a
 * third-party app calling /cart/add.js directly without requesting sections.
 * Whatever it writes is transient; the next server render overwrites it with
 * the authoritative value.
 *
 * KNOWN LIMIT: if the cart was empty, no bar exists in the DOM, and this script
 * cannot conjure one. Horizon's own flow re-renders the section and the bar
 * appears. A third-party direct add to an empty cart will not show the bar
 * until the next section render. Accepted, documented, not silently hidden.
 *
 * COPY lives in the Liquid snippet's data-msg-* / data-eyebrow-* attributes and
 * is read from there. Do not restate copy in this file.
 *
 * MATH must mirror the Liquid exactly. Liquid uses integer `divided_by`, which
 * floors, so this uses Math.floor. Using Math.round here would make a cart one
 * cent under the threshold render 100% via JS and 99% via Liquid.
 */

(function () {
  'use strict';

  // The script is loaded once from layout/theme.liquid, but guard anyway so a
  // duplicate include cannot double-wrap fetch.
  var globalScope = /** @type {Record<string, unknown>} */ (/** @type {unknown} */ (window));
  if (globalScope.__freakishShippingBarInit) return;
  globalScope.__freakishShippingBarInit = true;

  var SELECTOR = '[data-freakish-shipping-bar]';
  var AMOUNT_TOKEN = '[amount]';

  /**
   * Formats cents to match the Liquid `money` filter output for this shop.
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    var currency = 'USD';
    if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
      currency = Shopify.currency.active;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  /**
   * Replaces the message, wrapping the amount in <strong> to match the Liquid.
   * Builds real nodes rather than assigning innerHTML.
   * @param {HTMLElement} el
   * @param {string} template
   * @param {string|null} amount
   */
  function renderMessage(el, template, amount) {
    el.textContent = '';

    if (amount === null || template.indexOf(AMOUNT_TOKEN) === -1) {
      el.textContent = template;
      return;
    }

    var parts = template.split(AMOUNT_TOKEN);
    var strong = document.createElement('strong');
    strong.textContent = amount;

    el.appendChild(document.createTextNode(parts[0] || ''));
    el.appendChild(strong);
    el.appendChild(document.createTextNode(parts.slice(1).join(AMOUNT_TOKEN)));
  }

  /**
   * Recomputes one bar instance against a new cart total.
   * @param {HTMLElement} bar
   * @param {number} cartTotalCents
   */
  function updateBar(bar, cartTotalCents) {
    var threshold = parseInt(bar.dataset.threshold || '0', 10);
    if (!threshold || threshold <= 0) return;

    var eyebrowEl = /** @type {HTMLElement | null} */ (bar.querySelector('[data-shipping-bar-eyebrow]'));
    var messageEl = /** @type {HTMLElement | null} */ (bar.querySelector('[data-shipping-bar-message]'));
    var trackEl = /** @type {HTMLElement | null} */ (bar.querySelector('[data-shipping-bar-track]'));
    var fillEl = /** @type {HTMLElement | null} */ (bar.querySelector('[data-shipping-bar-fill]'));
    if (!eyebrowEl || !messageEl || !fillEl) return;

    var remaining = threshold - cartTotalCents;

    // Mirrors Liquid: cart_total | times: 100 | divided_by: threshold
    var pct = Math.floor((cartTotalCents * 100) / threshold);
    if (pct > 100) pct = 100;
    if (pct < 0) pct = 0;

    fillEl.style.width = pct + '%';
    if (trackEl) trackEl.setAttribute('aria-valuenow', String(pct));

    if (remaining > 0) {
      bar.classList.remove('freakish-shipping-bar--unlocked');
      eyebrowEl.textContent = bar.dataset.eyebrowUnder || '';
      renderMessage(messageEl, bar.dataset.msgUnder || '', formatMoney(remaining));
    } else {
      bar.classList.add('freakish-shipping-bar--unlocked');
      eyebrowEl.textContent = bar.dataset.eyebrowUnlocked || '';
      renderMessage(messageEl, bar.dataset.msgUnlocked || '', null);
    }

    bar.dataset.cartTotal = String(cartTotalCents);
  }

  /**
   * Updates every bar on the page. On /cart the drawer and the page each render
   * one, so there is more than one instance.
   * @param {unknown} cartTotalCents
   */
  function updateAll(cartTotalCents) {
    if (typeof cartTotalCents !== 'number' || !isFinite(cartTotalCents)) return;

    var bars = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < bars.length; i++) {
      updateBar(/** @type {HTMLElement} */ (bars[i]), cartTotalCents);
    }
  }

  // ── Horizon's own cart event ────────────────────────────────────────────
  // ThemeEvents.cartUpdate === 'cart:update'. CartUpdateEvent puts the new cart
  // object on detail.resource (see assets/events.js). Cheap and covers native
  // flows whose response did not happen to include our section.
  document.addEventListener('cart:update', function (event) {
    var detail = event && /** @type {CustomEvent} */ (event).detail;
    if (!detail) return;
    var resource = detail.resource;
    if (resource && typeof resource.total_price === 'number') {
      updateAll(resource.total_price);
    }
  });

  // ── Fallback tap for third-party direct Cart AJAX API calls ─────────────
  // Horizon-driven mutations never need this. It is here for apps that POST to
  // /cart/add.js and friends without requesting sections. The original response
  // is returned untouched; only a clone is read.
  /** @type {string[]} */
  var cartRoutes = [];
  if (typeof Theme !== 'undefined' && Theme.routes) {
    cartRoutes = [
      Theme.routes.cart_add_url,
      Theme.routes.cart_change_url,
      Theme.routes.cart_update_url,
    ].filter(Boolean);
  }

  /**
   * @param {string} url
   * @returns {boolean}
   */
  function isCartMutation(url) {
    if (!url) return false;
    for (var r = 0; r < cartRoutes.length; r++) {
      var route = cartRoutes[r];
      if (route && url.indexOf(route) !== -1) return true;
    }
    // Theme.routes omits /cart/clear.js, and covers the unlocalised paths only.
    return /\/cart\/(add|change|update|clear)(\.js|$)/.test(url);
  }

  var originalFetch = window.fetch;
  if (typeof originalFetch === 'function') {
    window.fetch = function (...args) {
      var result = originalFetch.apply(this, /** @type {Parameters<typeof fetch>} */ (args));

      // fetch accepts string | URL | Request. Reading `.url` only covers
      // Request; a URL object would have fallen through to '' and silently
      // skipped the tap, so handle all three explicitly.
      var url = '';
      try {
        var first = args[0];
        if (typeof first === 'string') {
          url = first;
        } else if (first instanceof URL) {
          url = first.href;
        } else if (first && typeof (/** @type {Request} */ (first).url) === 'string') {
          url = /** @type {Request} */ (first).url;
        }
      } catch (error) {
        url = '';
      }

      if (!isCartMutation(url)) return result;

      return result.then(function (response) {
        if (!response || !response.ok) return response;

        try {
          response
            .clone()
            .json()
            .then(function (data) {
              if (!data) return;
              // /cart/change.js and /cart/update.js return the cart itself.
              // /cart/add.js returns the added line item, so re-read the cart.
              if (typeof data.total_price === 'number' && typeof data.item_count === 'number') {
                updateAll(data.total_price);
              } else {
                refreshFromCartJson();
              }
            })
            .catch(function () {
              /* Not JSON, or already consumed. Nothing to do. */
            });
        } catch (error) {
          /* Response body not cloneable. Nothing to do. */
        }

        return response;
      });
    };
  }

  /**
   * Re-reads /cart.js when a mutation response did not carry a cart total.
   */
  function refreshFromCartJson() {
    var cartUrl = '/cart.js';
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) {
      cartUrl = Theme.routes.cart_url + '.js';
    }

    originalFetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (cart && typeof cart.total_price === 'number') updateAll(cart.total_price);
      })
      .catch(function () {
        /* Offline or blocked. The next section render corrects the bar. */
      });
  }
})();
